import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';

import type { loader } from '~/routes/_app.product.$urlSegment';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import { SectionHead } from '~/ui-rocco/pages/detail-shared/SectionHead';
import { useTrackProductProfileClick } from '../hooks/use-track-product-profile-click';
import { SupplierProductsRowContent } from './spec-accordion/SupplierProductsRowContent';

/**
 * (03) THE RANGE — the section head from Figma 149:13425: a small tracked
 * eyebrow, the title under it at 72px over two lines, and a line of standfirst
 * ranged right and sitting on the title's baseline.
 *
 * The supplier's catalogue is a page section rather than an accordion row now.
 * A row gives it a one-line label; the design gives it a head, and a grid of
 * twenty tiles was never really something to fold away.
 */
export const MoreProductsSection = ({
    runIn = 'max-md:pt-16 md:pt-[11.3125rem]',
}: {
    /**
     * The air above the head, as padding classes. The default matches the
     * Get more Specific section's bottom padding; a section set between the
     * two passes its own.
     */
    runIn?: string;
} = {}) => {
    const { product } = useLoaderData<typeof loader>();
    const supplier = product.Professional?.Title;
    const trackProfileClick = useTrackProductProfileClick();

    if (!supplier) return null;

    return (
        // The run-in is this section's own padding rather than a spacer on the
        // page: what sits above varies — the accordion and the installers rail
        // are both absent on most products — and a page-level spacer then
        // stacked against the one before it and doubled the gap. Matched to
        // the white Get-specific section's bottom padding above it.
        <section className={cn('bg-ground max-md:px-6 md:px-[3rem]', runIn)}>
            <div className={CONTENT_CAP_PADDED}>
                <SectionHead
                    lead="More products from"
                    subject={supplier}
                    mobileMutedSubject
                    hideMobileStandfirst
                    subjectLink={product.Professional?.Link}
                    onSubjectClick={trackProfileClick}
                    standfirst={`The rest of this range, and everything else ${supplier} makes.`}
                />

                <div className="max-md:mt-8 md:mt-[4rem]">
                    <SupplierProductsRowContent />
                </div>
            </div>
        </section>
    );
};
